import { useLayoutEffect } from "react";

/**
 * Reposition a portal dropdown after it renders, using real rendered size.
 *
 * Why: in portal + table UIs, measuring immediately can return 0x0 or stale size.
 * This uses useLayoutEffect (minimize flicker) + requestAnimationFrame (layout settled).
 */
export function useDropdownReposition({
  isOpen,
  anchorRect,
  dropdownRef,
  boundaryRect,
  getPosition,
  transformPosition,
  onPosition,
}) {
  useLayoutEffect(() => {
    if (!isOpen || !anchorRect) return;
    let rafId = requestAnimationFrame(() => {
      const el = dropdownRef?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const menuW = Math.ceil(r.width || el.offsetWidth || 0);
      const menuH = Math.ceil(r.height || el.offsetHeight || 0);
      if (!menuW || !menuH) return;

      const rawPos = getPosition(anchorRect, menuW, menuH, boundaryRect);
      if (!rawPos || typeof rawPos.left !== "number" || typeof rawPos.top !== "number") return;

      const pos = typeof transformPosition === "function"
        ? transformPosition({ ...rawPos, menuW, menuH, anchorRect, boundaryRect })
        : rawPos;

      if (pos && typeof pos.left === "number" && typeof pos.top === "number") {
        onPosition({ left: pos.left, top: pos.top });
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [isOpen, anchorRect, boundaryRect, dropdownRef, getPosition, transformPosition, onPosition]);
}

